import { API_BASE } from '@/shared/services/api';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Power, MapPin, Zap, Trophy, Bell, ChevronRight,
  CheckCircle, XCircle, Clock, Navigation, TrendingUp,
  Star, Flame, Target, Loader2
} from 'lucide-react';
import { MobileStatsCard, MobileButton, MobileCard, MobileBadge, MobileProgress } from '../components/MobileUIKit';
import { MobileBottomNav } from '../components/MobileBottomNav';
import { useMobileTheme } from '../context/MobileThemeContext';
import '../styles/mobile.css';

const API_URL = API_BASE;

export const DriverHomeDashboard = () => {
  const navigate = useNavigate();
  const { isDark, setTheme } = useMobileTheme();
  const [isOnline, setIsOnline] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [driverStats, setDriverStats] = useState({
    todayEarnings: 0,
    todayDeliveries: 0,
    rating: 4.5,
    weeklyEarnings: 0,
    onTimeRate: 95,
    acceptanceRate: 90
  });
  const [challenge, setChallenge] = useState({
    title: 'Complete 15 deliveries',
    reward: '+$20 bonus',
    current: 0,
    target: 15,
    endsIn: '4h 32m'
  });
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    const fetchDriverData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('ezerpToken');
        const headers = { 'Authorization': `Bearer ${token}` };
        
        const [statsRes, announcementsRes] = await Promise.all([
          fetch(`${API_URL}/api/driver/stats`, { headers }),
          fetch(`${API_URL}/api/driver/announcements`, { headers })
        ]);
        
        if (statsRes.ok) {
          const data = await statsRes.json();
          setDriverStats({
            todayEarnings: data.today_earnings || 0,
            todayDeliveries: data.today_deliveries || 0,
            rating: data.rating || 4.5,
            weeklyEarnings: data.weekly_earnings || 0,
            onTimeRate: data.on_time_rate || 95,
            acceptanceRate: data.acceptance_rate || 90
          });
          setChallenge(prev => ({
            ...prev,
            current: data.today_deliveries || 0
          }));
        }
        
        if (announcementsRes.ok) {
          const data = await announcementsRes.json();
          setAnnouncements(data.length > 0 ? data : [
            { id: 1, title: 'Multi-stop batching now available!', type: 'feature' },
            { id: 2, title: 'Weekend surge: 1.5x earnings', type: 'promo' }
          ]);
        }
      } catch (err) {
        console.error('Failed to fetch driver data:', err);
        // Keep defaults on error
        setAnnouncements([
          { id: 1, title: 'Multi-stop batching now available!', type: 'feature' },
          { id: 2, title: 'Weekend surge: 1.5x earnings', type: 'promo' }
        ]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDriverData();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const handleGoOnline = () => {
    setIsOnline(!isOnline);
    // Haptic feedback would go here in native app
  };

  if (loading) {
    return (
      <div className="mobile-app min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--ezp-primary)' }} />
      </div>
    );
  }

  return (
    <div className="mobile-app min-h-screen pb-20">
      {/* Header */}
      <div className="mobile-safe-top mobile-px-4 pt-4 pb-2">
        <div className="mobile-flex mobile-justify-between mobile-items-center">
          <div>
            <h1 className="mobile-text-title" style={{ color: 'var(--mobile-text-primary)' }}>
              {getGreeting()}, Carlos! 👋
            </h1>
            <p className="mobile-text-caption">Ready to earn today?</p>
          </div>
          <button 
            onClick={() => navigate('/driver/profile')}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: 'var(--mobile-bg-tertiary)' }}
          >
            <Bell className="w-5 h-5" style={{ color: 'var(--mobile-text-secondary)' }} />
          </button>
        </div>
      </div>

      {/* Go Online Button */}
      <div className="mobile-px-4 mobile-py-4">
        <MobileCard elevated className="text-center py-8">
          <button
            onClick={handleGoOnline}
            className={`
              mx-auto w-32 h-32 rounded-full flex items-center justify-center
              transition-all duration-300 transform active:scale-95
              ${isOnline 
                ? 'bg-[var(--mobile-success)]' 
                : 'bg-[var(--ezp-primary)]'
              }
            `}
            style={{
              boxShadow: isOnline 
                ? '0 0 40px rgba(76, 175, 80, 0.5)' 
                : '0 0 40px rgba(229, 57, 53, 0.4)'
            }}
          >
            <Power className="w-12 h-12 text-white" />
          </button>
          <p className="mobile-text-subtitle mt-4" style={{ color: 'var(--mobile-text-primary)' }}>
            {isOnline ? "You're Online" : 'Go Online'}
          </p>
          <p className="mobile-text-caption mt-1">
            {isOnline ? 'Receiving orders...' : 'Tap to start receiving orders'}
          </p>
        </MobileCard>
      </div>

      {/* Quick Stats */}
      <div className="mobile-px-4 mobile-mb-4">
        <div className="grid grid-cols-3 gap-3">
          <MobileStatsCard
            value={`$${driverStats.todayEarnings.toFixed(2)}`}
            label="Today"
            trend={{ direction: 'up', value: '15%' }}
            color="earnings"
          />
          <MobileStatsCard
            value={driverStats.todayDeliveries}
            label="Deliveries"
            trend={{ direction: 'up', value: '+3' }}
            color="success"
          />
          <MobileStatsCard
            value={driverStats.rating}
            label="Rating"
            icon={Star}
            color="default"
          />
        </div>
      </div>

      {/* Surge/Zone Info */}
      {isOnline && (
        <div className="mobile-px-4 mobile-mb-4 mobile-animate-fade-in">
          <MobileCard className="mobile-flex mobile-items-center mobile-gap-3">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: 'var(--mobile-warning-muted)' }}
            >
              <Zap className="w-5 h-5" style={{ color: 'var(--mobile-warning)' }} />
            </div>
            <div className="flex-1">
              <p className="mobile-text-body font-semibold" style={{ color: 'var(--mobile-text-primary)' }}>
                🔥 Surge Active: 1.5x
              </p>
              <p className="mobile-text-caption">Downtown area until 7:00 PM</p>
            </div>
            <ChevronRight className="w-5 h-5" style={{ color: 'var(--mobile-text-tertiary)' }} />
          </MobileCard>
        </div>
      )}

      {/* Current Location */}
      <div className="mobile-px-4 mobile-mb-4">
        <div 
          className="mobile-flex mobile-items-center mobile-gap-2 mobile-py-2"
          style={{ color: 'var(--mobile-text-secondary)' }}
        >
          <MapPin className="w-4 h-4" />
          <span className="mobile-text-caption">Current Zone: Downtown</span>
        </div>
      </div>

      {/* Daily Challenge */}
      <div className="mobile-px-4 mobile-mb-4">
        <MobileCard elevated interactive onClick={() => navigate('/driver/earnings')}>
          <div className="mobile-flex mobile-items-center mobile-gap-3 mb-3">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)' }}
            >
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="mobile-text-body font-semibold" style={{ color: 'var(--mobile-text-primary)' }}>
                DAILY CHALLENGE
              </p>
              <p className="mobile-text-caption">{challenge.title} for {challenge.reward}</p>
            </div>
          </div>
          <MobileProgress value={challenge.current} max={challenge.target} className="mb-2" />
          <div className="mobile-flex mobile-justify-between">
            <span className="mobile-text-tiny">{challenge.current}/{challenge.target} completed</span>
            <span className="mobile-text-tiny">⏰ Ends in {challenge.endsIn}</span>
          </div>
        </MobileCard>
      </div>

      {/* Performance Stats */}
      <div className="mobile-px-4 mobile-mb-4">
        <h3 className="mobile-text-subtitle mb-3" style={{ color: 'var(--mobile-text-primary)' }}>
          Your Performance
        </h3>
        <div className="grid grid-cols-2 gap-3">
          <MobileCard>
            <div className="mobile-flex mobile-items-center mobile-gap-2 mb-2">
              <Clock className="w-4 h-4" style={{ color: 'var(--mobile-success)' }} />
              <span className="mobile-text-caption">On-Time Rate</span>
            </div>
            <p className="mobile-text-title" style={{ color: 'var(--mobile-success)' }}>
              {driverStats.onTimeRate}%
            </p>
          </MobileCard>
          <MobileCard>
            <div className="mobile-flex mobile-items-center mobile-gap-2 mb-2">
              <Target className="w-4 h-4" style={{ color: 'var(--mobile-info)' }} />
              <span className="mobile-text-caption">Acceptance</span>
            </div>
            <p className="mobile-text-title" style={{ color: 'var(--mobile-info)' }}>
              {driverStats.acceptanceRate}%
            </p>
          </MobileCard>
        </div>
      </div>

      {/* Streak */}
      <div className="mobile-px-4 mobile-mb-4">
        <MobileCard>
          <div className="mobile-flex mobile-items-center mobile-gap-3">
            <Flame className="w-6 h-6" style={{ color: '#FF6B35' }} />
            <div className="flex-1">
              <p className="mobile-text-body font-semibold" style={{ color: 'var(--mobile-text-primary)' }}>
                7 Day Streak! 🔥
              </p>
              <p className="mobile-text-caption">Keep going for 14-day bonus</p>
            </div>
          </div>
          <div className="mobile-flex mobile-gap-2 mt-3">
            {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, i) => (
              <div 
                key={i}
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold
                  ${i < 7 ? 'bg-[var(--mobile-success)] text-white' : ''}
                `}
                style={{ 
                  background: i >= 7 ? 'var(--mobile-bg-tertiary)' : undefined,
                  color: i >= 7 ? 'var(--mobile-text-tertiary)' : undefined
                }}
              >
                {i < 7 ? '✓' : day}
              </div>
            ))}
          </div>
        </MobileCard>
      </div>

      {/* Announcements */}
      <div className="mobile-px-4 mobile-mb-4">
        <h3 className="mobile-text-subtitle mb-3" style={{ color: 'var(--mobile-text-primary)' }}>
          Announcements
        </h3>
        {announcements.map(ann => (
          <MobileCard key={ann.id} interactive className="mb-2">
            <div className="mobile-flex mobile-items-center mobile-gap-3">
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ 
                  background: ann.type === 'promo' 
                    ? 'var(--mobile-earnings-glow)' 
                    : 'rgba(66, 165, 245, 0.2)' 
                }}
              >
                {ann.type === 'promo' ? '💰' : '✨'}
              </div>
              <p className="mobile-text-body flex-1" style={{ color: 'var(--mobile-text-primary)' }}>
                {ann.title}
              </p>
              <ChevronRight className="w-4 h-4" style={{ color: 'var(--mobile-text-tertiary)' }} />
            </div>
          </MobileCard>
        ))}
      </div>

      {/* Theme Toggle (for testing) */}
      <div className="mobile-px-4 mobile-mb-24">
        <button
          onClick={() => setTheme(isDark ? 'light' : 'dark')}
          className="mobile-btn mobile-btn-ghost mobile-btn-full"
          style={{ color: 'var(--mobile-text-secondary)' }}
        >
          {isDark ? '☀️ Switch to Light Mode' : '🌙 Switch to Dark Mode'}
        </button>
      </div>

      {/* Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
};

export default DriverHomeDashboard;
